import { computed, onScopeDispose, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { IssueSeverity, IssueType } from '@/enums/issue'
import { t } from '@/libs/i18n'
import { IssueFilterParam, issuesFilteredRoute } from '@/libs/issue'
import { useConditionalGet, usePoll } from '@/libs/polling'
import type { IDashboard } from '@/types/Dashboard'

/**
 * §7.1's dashboard, as **one** document polled **once per tab** (§6.1, §9.2, W11.4).
 *
 * # Why the feed is shared, and why the poll is not in a store
 *
 * Two surfaces read this document: the dashboard screen and the sidebar's Issues badge (§9.1), and
 * the badge is the panel's only alerting surface (§7.5) — so it has to stay live on every screen,
 * not only while `/dashboard` is open. That means the poll belongs to the **shell**, which is
 * mounted for the whole session, and the state it fills has to outlive the dashboard view.
 *
 * §9.2 is equally explicit that a poll started in a store leaks: nothing unmounts a store, so a
 * timer started there survives sign-out and keeps a fleet of tabs polling forever. The resolution
 * is the shape `layouts/layout.ts` already uses for the sidebar's own state — module-scoped refs,
 * written by a `usePoll` that a **component** owns and disposes with. `Default.vue` calls
 * [useDashboardPoll] exactly once; everything else calls [useDashboardFeed] and only reads.
 *
 * The result is the acceptance criterion stated literally: *one aggregated request per tab per
 * 15 s, mostly 304s*. Two pollers — one for the badge, one for the screen — would have been two.
 */

/** §9.2's table: the dashboard polls `GET /dashboard` every 15 s. */
export const DashboardPollMs = 15_000

/** §6.1's noun. One path, spelled once, so the feed and any future caller cannot disagree. */
const DashboardPath = '/dashboard'

// ---------------------------------------------------------------------------------------------
// The shared document
// ---------------------------------------------------------------------------------------------

/** The last document that arrived, or null before the first answer of this session. */
const document = ref<IDashboard | null>(null)
/** Epoch millis of the last *answered* poll — a 304 counts, because "unchanged as of now" is news. */
const answeredAt = ref(0)
/** True when the most recent poll did not answer. The previous document stays on screen. */
const failed = ref(false)

/**
 * The shell's `trigger`, published so a screen can force a tick after something it just changed
 * rather than waiting out fifteen seconds. It is a plain module variable and not a ref: nothing
 * renders it, and a reactive function handle would only invite somebody to call it from a template.
 */
let triggerPoll: (() => Promise<void>) | null = null

const conditional = useConditionalGet<IDashboard>()

/**
 * Starts the one poll — **called exactly once, by `layouts/Default.vue`**.
 *
 * # It resets before its first tick, and that is a privacy rule rather than hygiene
 *
 * Every number in this document is scope-filtered (D47), so it describes one operator's slice of
 * the fleet. Sign-out unmounts the shell and sign-in mounts a fresh one, so clearing here is what
 * guarantees a second user on the same tab never sees the first user's totals — not even for the
 * one frame between mount and the first answer. The validator is dropped with the body for the
 * same reason: a tag is a claim about a document this session has not been served.
 */
export const useDashboardPoll = () => {
    document.value   = null
    answeredAt.value = 0
    failed.value     = false
    conditional.reset()

    const tick = async () => {
        const answer = await conditional.get(DashboardPath)

        // A 304 is a successful poll and deliberately touches nothing but the clock: no assignment
        // means no reactive write, which means no re-render — which is the entire point of the
        // validator on a document forty tabs are re-reading every fifteen seconds (§9.2).
        if (answer.unchanged) {
            answeredAt.value = Date.now()
            failed.value     = false

            return
        }

        if (!answer.ok || !answer.data) {
            // A transient failure keeps the last good document on screen. A dashboard that blanked
            // itself because one request lost a race with a redeploy is a dashboard an operator
            // stops trusting during exactly the incident it exists for.
            failed.value = true

            return
        }

        document.value   = answer.data
        answeredAt.value = Date.now()
        failed.value     = false
    }

    const poll = usePoll(tick, { interval: DashboardPollMs })

    triggerPoll = poll.trigger
    onScopeDispose(() => {
        if (triggerPoll === poll.trigger) triggerPoll = null
    })

    return poll
}

export interface DashboardFeed {
    dashboard: Ref<IDashboard | null>
    /** True once any poll has answered — the difference between "nothing is wrong" and "nothing is known". */
    loaded:    ComputedRef<boolean>
    failed:    Ref<boolean>
    /** Open **critical** issues in this caller's scope — §9.1's sidebar badge, and nothing else. */
    critical:  ComputedRef<number>
    /** Force a tick now, through the shell's poller. A no-op when the shell is not mounted. */
    refresh:   () => Promise<void>
}

/** Read-only access to the shell's document. Safe to call from anywhere, as often as one likes. */
export const useDashboardFeed = (): DashboardFeed => ({
    dashboard: document,
    loaded:    computed(() => answeredAt.value > 0),
    failed,
    critical:  computed(() => document.value?.issues.critical ?? 0),
    refresh:   () => triggerPoll ? triggerPoll() : Promise.resolve(),
})

// ---------------------------------------------------------------------------------------------
// The stat tiles (§7.1, W11.4)
// ---------------------------------------------------------------------------------------------

/**
 * One tile: a label, a number, and the list that number was counted over.
 *
 * The `to` is the requirement rather than a nicety — W11.4 asks for tiles that link **into
 * pre-filtered lists**, because a dashboard that only states a number leaves the operator to go and
 * find the rows themselves, which is the motion the whole screen exists to remove.
 */
export interface DashboardTile {
    key:   string
    label: string
    value: number
    icon:  string
    /** Semantic only (§9.5). `neutral` is a count, not a verdict — never a colour picked for looks. */
    color: 'neutral' | 'success' | 'warning' | 'error' | 'info'
    to:    RouteLocationRaw
    /** One short line under the number, where the destination is not obvious from the label. */
    hint?: string
}

/**
 * §7.1's basic metrics as six tiles, each with a destination.
 *
 * # Which destinations are genuinely *filtered*, and why the other two are lists
 *
 * Four of the six land on a filtered page: the issue counts filter `/issues` by severity, and
 * *Offline* filters it by `instance_offline` — which is the exact list of sites that are down,
 * carrying the deep link into each one. Instances and Clients land on their own lists unfiltered,
 * for the reason `DashboardTotals` gives on the clients tile: the total is counted over exactly
 * that list under exactly the caller's scope, so the tile and the screen it opens agree, and a
 * filter would make them disagree.
 *
 * There is deliberately **no** *Healthy* tile linking to a filtered fleet list. `GET /instances`
 * serves instance documents and health lives in `instance_states`, so such a filter does not exist
 * — and building it in the browser would need one state read per row, which is precisely the
 * fan-out §6.1 forbids. The healthy / degraded / pending counts are shown as the split inside the
 * Instances tile instead, where they are facts rather than false links.
 *
 * Groups have no fleet-wide screen at all (a group lives inside an instance), so that tile opens
 * the instance list and says so in its hint rather than pretending to a page that does not exist.
 */
export const dashboardTiles = (dashboard: IDashboard | null): DashboardTile[] => {
    const totals = dashboard?.totals
    const issues = dashboard?.issues

    return [
        {
            key:   'instances',
            label: t('dashboard.tiles.instances'),
            value: totals?.instances ?? 0,
            icon:  'i-lucide-server',
            color: 'neutral',
            to:    { name: 'instances' },
        },
        {
            key:   'offline',
            label: t('dashboard.tiles.offline'),
            value: totals?.offline_instances ?? 0,
            icon:  'i-lucide-wifi-off',
            color: 'error',
            to:    issuesFilteredRoute({ [IssueFilterParam.Type]: IssueType.InstanceOffline }),
            hint:  t('dashboard.tiles.offline_hint'),
        },
        {
            key:   'open_issues',
            label: t('dashboard.tiles.open_issues'),
            value: issues?.open ?? 0,
            icon:  'i-lucide-triangle-alert',
            color: 'neutral',
            to:    { name: 'issues' },
        },
        {
            key:   'critical_issues',
            label: t('dashboard.tiles.critical_issues'),
            value: issues?.critical ?? 0,
            icon:  'i-lucide-siren',
            color: 'error',
            to:    issuesFilteredRoute({ [IssueFilterParam.Severity]: IssueSeverity.Critical }),
        },
        {
            key:   'clients',
            label: t('dashboard.tiles.clients'),
            value: totals?.clients ?? 0,
            icon:  'i-lucide-building-2',
            color: 'neutral',
            to:    { name: 'clients' },
        },
        {
            key:   'groups',
            label: t('dashboard.tiles.groups'),
            value: totals?.groups ?? 0,
            icon:  'i-lucide-boxes',
            color: 'neutral',
            to:    { name: 'instances' },
            hint:  t('dashboard.tiles.groups_hint'),
        },
    ]
}

/** One slice of the fleet split shown inside the Instances tile. */
export interface FleetSplitEntry {
    key:   string
    label: string
    value: number
    /** Semantic, and the four are mutually exclusive: they sum to the tile's own total. */
    color: 'success' | 'warning' | 'error' | 'neutral'
}

/**
 * The four-way split of §7.1, in the order an operator triages it: what is fine, what is wrong,
 * what is silent, what was never installed.
 *
 * `pending` is last and `neutral` on purpose — an instance that has never reported is **not**
 * offline (§9.5), and colouring it as a fault would have somebody chasing an outage on a site
 * nobody has installed yet.
 */
export const fleetSplit = (dashboard: IDashboard | null): FleetSplitEntry[] => {
    const totals = dashboard?.totals

    return [
        { key: 'healthy',  label: t('dashboard.split.healthy'),  value: totals?.healthy_instances  ?? 0, color: 'success' },
        { key: 'degraded', label: t('dashboard.split.degraded'), value: totals?.degraded_instances ?? 0, color: 'warning' },
        { key: 'offline',  label: t('dashboard.split.offline'),  value: totals?.offline_instances  ?? 0, color: 'error' },
        { key: 'pending',  label: t('dashboard.split.pending'),  value: totals?.pending_instances  ?? 0, color: 'neutral' },
    ]
}
